import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import ReactPlayer from 'react-player'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { downloadCertificate } from '../../../utils/helpers'
import { getPurchasedCourseDetail } from '../../../api/user'
import { markLessonAsWatched } from '../../../api/courses'

const CourseContent = () => {
  const [showCertificateModal, setShowCertificateModal] = useState(false)
  const [completedLessons, setCompletedLessons] = useState<number[]>([])
  const [certificateName, setCertificateName] = useState('')
  const [selectedLesson, setSelectedLesson] = useState<any>(null)
  const [isFinishingCourse, setIsFinishingCourse] = useState(false)
  const [finishProgress, setFinishProgress] = useState({ current: 0, total: 0 })
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  // Fetch course data using the new API
  const { data: courseResponse, isLoading, isError, error } = useQuery({
    queryKey: ['purchasedCourseDetail', id],
    queryFn: () => getPurchasedCourseDetail(id!),
    enabled: !!id,
  })

  const course = courseResponse?.data

  // Mutation for marking lesson as watched
  const markLessonMutation = useMutation({
    mutationFn: markLessonAsWatched,
    onSuccess: () => {
      // Invalidate and refetch course data to get updated progress and is_watched status
      queryClient.invalidateQueries({ queryKey: ['purchasedCourseDetail', id] })
    },
    onError: (error) => {
      console.error('Error marking lesson as watched:', error)
      // Revert the local state change on error
      setCompletedLessons((prev) =>
        prev.filter((lessonId) => lessonId !== markLessonMutation.variables),
      )
    },
  })

  // Set default video URL from first lesson if available
  const videoUrl =
    selectedLesson?.video_link ||
    course?.topics?.[0]?.lessons?.[0]?.video_link ||
    ''

  // Set first lesson as default when course data is loaded
  useEffect(() => {
    if (course?.topics?.[0]?.lessons?.[0] && !selectedLesson) {
      setSelectedLesson(course.topics[0].lessons[0])
    }
  }, [course, selectedLesson])

  // Initialize completedLessons with lessons that have is_watched: true
  // This ensures that lessons marked as watched on the server are automatically checked
  useEffect(() => {
    if (course?.topics) {
      const watchedLessonIds: number[] = []
      course.topics.forEach((topic) => {
        topic.lessons?.forEach((lesson) => {
          if (lesson.is_watched) {
            watchedLessonIds.push(lesson.id)
          }
        })
      })
      setCompletedLessons(watchedLessonIds)
    }
  }, [course])

  const handleLessonSelect = (lesson: any) => {
    setSelectedLesson(lesson)
  }

  const handleCertificateDownload = async () => {
    // Check if certificate_downloads exists and has a name
    if (course?.certificate_downloads && course.certificate_downloads.name) {
      // Direct download with existing name
      console.log(
        'Downloading certificate with existing name:',
        course.certificate_downloads.name,
      )
      const courseTopics =
        course?.studied?.map((topic) => ({ name: topic })) || []
      await downloadCertificate(
        course.certificate_downloads.name ||
          course?.title ||
          'Course Certificate',
        undefined, // customFileName
        undefined, // pdfUrl
        course.certificate_downloads.name, // userName
        course?.title, // courseTitle
        true, // useAutoGeneration
        course?.id, // courseId
        courseTopics, // courseTopics
      )
    } else {
      // Show modal for first time download (certificate_downloads is null)
      setShowCertificateModal(true)
    }
  }

  const confirmCertificateDownload = async () => {
    setShowCertificateModal(false)
    // Download logic here
    console.log('Downloading certificate with name:', certificateName)
    // Extract topics data for certificate
    const courseTopics =
      course?.studied?.map((topic) => ({ name: topic })) || []
    await downloadCertificate(
      certificateName || course?.title || 'Course Certificate',
      undefined, // customFileName
      undefined, // pdfUrl
      certificateName, // userName
      course?.title, // courseTitle
      true, // useAutoGeneration
      course?.id, // courseId
      courseTopics, // courseTopics
    )
  }

  const handleLessonComplete = (lessonId: number) => {
    const isCurrentlyCompleted = completedLessons.includes(lessonId)

    // Update local state immediately for better UX
    setCompletedLessons((prev) =>
      isCurrentlyCompleted
        ? prev.filter((id) => id !== lessonId)
        : [...prev, lessonId],
    )

    // Only call API when marking as completed (not when unchecking)
    if (!isCurrentlyCompleted) {
      markLessonMutation.mutate(lessonId)
    }
  }

  const calculateProgress = () => {
    if (!course?.topics) return 0
    const totalLessons = course.topics.reduce(
      (acc: number, topic: any) => acc + (topic.lessons?.length || 0),
      0,
    )
    return totalLessons > 0
      ? Math.round((completedLessons.length / totalLessons) * 100)
      : 0
  }

  const handleJoinGroup = () => {
    if (course?.link_group) {
      window.open(course.link_group, '_blank')
    }
  }

  const handleDownloadMaterial = () => {
    if (course?.link_ebook) {
      window.open(course.link_ebook, '_blank')
    }
  }

  // Get all unwatched lessons
  const getUnwatchedLessons = () => {
    if (!course?.topics) return []
    const unwatchedLessons: number[] = []
    course.topics.forEach((topic) => {
      topic.lessons?.forEach((lesson) => {
        if (!lesson.is_watched && !completedLessons.includes(lesson.id)) {
          unwatchedLessons.push(lesson.id)
        }
      })
    })
    return unwatchedLessons
  }

  // Get unwatched lessons count for display
  const unwatchedCount = getUnwatchedLessons().length

  // Check if course is completed
  const isCourseCompleted = () => {
    if (!course?.topics) return false
    const totalLessons = course.topics.reduce(
      (acc: number, topic: any) => acc + (topic.lessons?.length || 0),
      0,
    )
    return completedLessons.length >= totalLessons
  }

  // Finish course by marking all unwatched lessons as watched
  const handleFinishCourse = async () => {
    const unwatchedLessons = getUnwatchedLessons()
    if (unwatchedLessons.length === 0) return

    setIsFinishingCourse(true)
    setFinishProgress({ current: 0, total: unwatchedLessons.length })

    try {
      // Process lessons in batches to avoid overwhelming the server
      const batchSize = 3
      for (let i = 0; i < unwatchedLessons.length; i += batchSize) {
        const batch = unwatchedLessons.slice(i, i + batchSize)

        // Process batch concurrently
        const promises = batch.map(async (lessonId) => {
          try {
            await markLessonAsWatched(lessonId)
            // Update local state immediately for better UX
            setCompletedLessons((prev) => [...prev, lessonId])
            setFinishProgress((prev) => ({
              ...prev,
              current: prev.current + 1,
            }))
          } catch (error) {
            console.error(`Error marking lesson ${lessonId} as watched:`, error)
          }
        })

        await Promise.all(promises)

        // Small delay between batches to be gentle on the server
        if (i + batchSize < unwatchedLessons.length) {
          await new Promise((resolve) => setTimeout(resolve, 500))
        }
      }

      // Refresh course data to get updated progress
      queryClient.invalidateQueries({ queryKey: ['purchasedCourseDetail', id] })
    } catch (error) {
      console.error('Error finishing course:', error)
    } finally {
      setIsFinishingCourse(false)
      setFinishProgress({ current: 0, total: 0 })
    }
  }

  // Helpers to navigate between lessons across topics
  const findCurrentPosition = () => {
    if (!course?.topics || !selectedLesson) return null
    for (let topicIndex = 0; topicIndex < course.topics.length; topicIndex++) {
      const lessons = course.topics[topicIndex]?.lessons || []
      for (let lessonIndex = 0; lessonIndex < lessons.length; lessonIndex++) {
        if (lessons[lessonIndex]?.id === selectedLesson.id) {
          return { topicIndex, lessonIndex }
        }
      }
    }
    return null
  }

  const getPrevLesson = () => {
    if (!course?.topics || !selectedLesson) return null
    const pos = findCurrentPosition()
    if (!pos) return null
    const { topicIndex, lessonIndex } = pos
    if (lessonIndex > 0) {
      return course.topics[topicIndex].lessons[lessonIndex - 1]
    }
    if (topicIndex > 0) {
      const prevTopicLessons = course.topics[topicIndex - 1]?.lessons || []
      if (prevTopicLessons.length > 0) {
        return prevTopicLessons[prevTopicLessons.length - 1]
      }
    }
    return null
  }

  const getNextLesson = () => {
    if (!course?.topics || !selectedLesson) return null
    const pos = findCurrentPosition()
    if (!pos) return null
    const { topicIndex, lessonIndex } = pos
    const currentTopicLessons = course.topics[topicIndex]?.lessons || []
    if (lessonIndex + 1 < currentTopicLessons.length) {
      return currentTopicLessons[lessonIndex + 1]
    }
    if (topicIndex + 1 < course.topics.length) {
      const nextTopicLessons = course.topics[topicIndex + 1]?.lessons || []
      if (nextTopicLessons.length > 0) {
        return nextTopicLessons[0]
      }
    }
    return null
  }

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading course content...</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-5 text-danger">
        <p>Error loading course: {error?.message || 'Unknown error'}</p>
        <p>Please try again later.</p>
      </div>
    )
  }

  if (!course) {
    return <div>Course not found.</div>
  }

  return (
    <>
      {/* Course Content */}
      <div className="content pt-0">
        <div className="container">
          <div className="course-watch-section">
            <div className="row">
              <div className="col-lg-4 border-end">
                <div
                  className="progress-overview-section"
                  style={{ padding: '0px' }}
                >
                  <div className="mb-4"></div>
                  <h3>{course.title}</h3>
                  <div className="mb-4">
                    <p className="mb-1">{calculateProgress()}% Complete</p>
                    <div
                      className="progress progress-xs mb-2"
                      role="progressbar"
                      aria-valuenow={calculateProgress()}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className="progress-bar bg-success"
                        style={{ width: `${calculateProgress()}%` }}
                      />
                    </div>
                    <span className="fw-medium">
                      Last activity on{' '}
                      {new Date().toLocaleDateString('id-ID', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="mb-4">
                    <div className="d-grid gap-2">
                      {course.link_group && (
                        <button
                          className="btn btn-outline-primary"
                          onClick={handleJoinGroup}
                        >
                          <i className="isax isax-people me-2" />
                          Join Group
                        </button>
                      )}
                      <button
                        className="btn btn-success"
                        onClick={handleCertificateDownload}
                      >
                        <i className="isax isax-document-download me-2" />
                        Download Sertifikat
                      </button>
                      {course.link_ebook && (
                        <button
                          className="btn btn-info"
                          onClick={handleDownloadMaterial}
                        >
                          <i className="isax isax-document-download me-2" />
                          Download Materi
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Course Topics */}
                  <div
                    className="accordions-items-seperate"
                    id="accordionSpacingExample"
                  >
                    {course.topics?.map((topic: any, topicIndex: number) => (
                      <div className="accordion-item" key={topic.id}>
                        <div
                          className="accordion-header"
                          id={`headingSpacing${topicIndex}`}
                        >
                          <div
                            className="accordion-button collapsed"
                            role="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#Spacing${topicIndex}`}
                            aria-expanded="false"
                            aria-controls={`Spacing${topicIndex}`}
                          >
                            <div>
                              <span className="d-block mb-1">
                                Topic {topicIndex + 1}
                              </span>
                              <h6 className="mb-0">{topic.name}</h6>
                            </div>
                          </div>
                        </div>
                        <div
                          id={`Spacing${topicIndex}`}
                          className="accordion-collapse collapse"
                          aria-labelledby={`headingSpacing${topicIndex}`}
                          data-bs-parent="#accordionSpacingExample"
                        >
                          <div className="accordion-body">
                            {topic.lessons?.map(
                              (lesson: any, lessonIndex: number) => (
                                <div
                                  className={`d-flex align-items-center justify-content-between mb-3 lesson-item ${
                                    selectedLesson?.id === lesson.id
                                      ? 'selected'
                                      : ''
                                  }`}
                                  key={lesson.id}
                                  onClick={() => handleLessonSelect(lesson)}
                                  style={{
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    backgroundColor:
                                      selectedLesson?.id === lesson.id
                                        ? '#f8f9fa'
                                        : 'transparent',
                                    border:
                                      selectedLesson?.id === lesson.id
                                        ? '1px solid #dee2e6'
                                        : '1px solid transparent',
                                  }}
                                >
                                  <div className="d-flex align-items-center">
                                    <span className="d-flex">
                                      <i
                                        className={`isax ${
                                          selectedLesson?.id === lesson.id
                                            ? 'isax-play-circle5 text-primary'
                                            : 'isax-play-circle5 text-success'
                                        } fs-24 me-1`}
                                      />
                                    </span>
                                    <p className="accordian-content mb-0">
                                      {lesson.name}
                                    </p>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <div className="form-check d-flex align-items-center">
                                      <input
                                        className="form-check-input me-2"
                                        type="checkbox"
                                        checked={completedLessons.includes(
                                          lesson.id,
                                        )}
                                        disabled={markLessonMutation.isPending}
                                        onChange={(e) => {
                                          e.stopPropagation()
                                          handleLessonComplete(lesson.id)
                                        }}
                                      />
                                      <span
                                        className={`d-inline-flex align-items-center ${
                                          completedLessons.includes(lesson.id)
                                            ? 'text-success'
                                            : ''
                                        }`}
                                      >
                                        {markLessonMutation.isPending &&
                                        markLessonMutation.variables ===
                                          lesson.id ? (
                                          <div
                                            className="spinner-border spinner-border-sm me-1"
                                            role="status"
                                          >
                                            <span className="visually-hidden">
                                              Loading...
                                            </span>
                                          </div>
                                        ) : (
                                          <i
                                            className={`isax me-1 ${
                                              completedLessons.includes(
                                                lesson.id,
                                              )
                                                ? 'isax-tick-circle'
                                                : 'isax-circle'
                                            }`}
                                          />
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Finish Course Button */}
                  {!isCourseCompleted() && unwatchedCount > 0 && (
                    <div className="mt-4 p-3 border rounded bg-light">
                      <div className="text-center">
                        <h6 className="mb-2">Complete All Remaining Lessons</h6>
                        <p className="text-muted small mb-3">
                          Mark all {unwatchedCount} remaining lessons as watched
                        </p>
                        <button
                          className="btn btn-success btn-lg"
                          onClick={handleFinishCourse}
                          disabled={isFinishingCourse}
                        >
                          {isFinishingCourse ? (
                            <>
                              <div
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                              Finishing Course... ({finishProgress.current}/
                              {finishProgress.total})
                            </>
                          ) : (
                            <>
                              <i className="isax isax-tick-circle me-2" />
                              Finish Course
                            </>
                          )}
                        </button>
                        {isFinishingCourse && (
                          <div className="mt-3">
                            <div className="progress" style={{ height: '6px' }}>
                              <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{
                                  width: `${
                                    finishProgress.total > 0
                                      ? (finishProgress.current /
                                          finishProgress.total) *
                                        100
                                      : 0
                                  }%`,
                                }}
                                aria-valuenow={finishProgress.current}
                                aria-valuemin={0}
                                aria-valuemax={finishProgress.total}
                              />
                            </div>
                            <small className="text-muted mt-1 d-block">
                              Processing lessons... {finishProgress.current} of{' '}
                              {finishProgress.total} completed
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Course Completed Message */}
                  {isCourseCompleted() && (
                    <div className="mt-4 p-3 border rounded bg-success bg-opacity-10">
                      <div className="text-center">
                        <i className="isax isax-tick-circle text-success fs-1 mb-2" />
                        <h6 className="text-success mb-2">Course Completed!</h6>
                        <p className="text-muted small mb-0">
                          Congratulations! You have completed all lessons in
                          this course.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-lg-8">
                <div className="course-watch-content">
                  {/* Title, Label, and Duration above the player */}
                  <div className="mb-3">
                    <h4 className="mb-1">
                      {selectedLesson?.name || course.title}
                    </h4>
                    <div className="d-flex align-items-center">
                      {/* <span className="badge bg-primary me-2">
                        {selectedLesson?.is_premium ? 'Premium' : 'Free'}
                      </span> */}
                      <small className="text-muted">
                        {selectedLesson?.description || ''}
                      </small>
                    </div>
                  </div>
                  <div className="video-player-container d-flex flex-row align-items-center justify-content-center">
                    {videoUrl ? (
                      <ReactPlayer
                        url={videoUrl}
                        controls={true}
                        key={videoUrl}
                        style={{
                          backgroundColor: '#000',
                          width: '100%',
                          height: 'auto',
                          aspectRatio: '16/9',
                        }}
                        config={{
                          youtube: {
                            playerVars: {
                              modestbranding: 1,
                              rel: 0,
                            },
                          },
                        }}
                      />
                    ) : (
                      <div
                        className="d-flex flex-column align-items-center justify-content-center text-muted"
                        style={{
                          backgroundColor: '#f8f9fa',
                          width: '100%',
                          height: '400px',
                          aspectRatio: '16/9',
                          border: '2px dashed #dee2e6',
                          borderRadius: '8px',
                        }}
                      >
                        <i
                          className="isax isax-video-play text-muted"
                          style={{ fontSize: '48px', marginBottom: '16px' }}
                        />
                        <h5 className="text-muted mb-2">
                          Video Tidak Tersedia
                        </h5>
                        <p className="text-muted mb-0">
                          Video untuk lesson ini belum tersedia
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Prev / Next Controls */}
                  {course?.topics && (
                    <div
                      className="d-flex flex-row align-items-center justify-content-between gap-3 flex-wrap"
                      style={{ marginTop: 64 }}
                    >
                      <button
                        className="btn btn-outline-secondary"
                        disabled={!getPrevLesson()}
                        onClick={() => {
                          const prev = getPrevLesson()
                          if (prev) setSelectedLesson(prev)
                        }}
                      >
                        <i className="isax isax-arrow-left me-2" /> Prev
                      </button>
                      <button
                        className="btn btn-primary"
                        disabled={!getNextLesson()}
                        onClick={() => {
                          const next = getNextLesson()
                          if (next) setSelectedLesson(next)
                        }}
                      >
                        Next <i className="isax isax-arrow-right ms-2" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Certificate Download Modal */}
      {showCertificateModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Download Sertifikat</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCertificateModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Apakah Anda yakin ingin mencetak e-sertifikat dengan nama
                    yang tertera pada formulir di bawah ini?
                  </p>
                  <div className="mb-3">
                    <label htmlFor="certificateName" className="form-label">
                      Nama pada Sertifikat
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="certificateName"
                      value={certificateName}
                      onChange={(e) => setCertificateName(e.target.value)}
                      placeholder="Masukkan nama lengkap"
                    />
                  </div>
                  <div className="alert alert-warning">
                    <i className="isax isax-warning-2 me-2" />
                    Pastikan nama sudah benar, karena e-sertifikat hanya dapat
                    di generate satu kali.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCertificateModal(false)}
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmCertificateDownload}
                    disabled={!certificateName.trim()}
                  >
                    Download Sertifikat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default CourseContent
